import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AgentOfficeAddressDto, AgentProfileDto, OpeningHoursDto } from '@workspace/data-types';
import { AuraOpeningHoursDisplayComponent } from '@workspace/ng-aura';

export interface ResolvedAddressDisplay {
  line1: string;
  line2: string;
  complement?: string;
  isCustom: boolean;
  sourceLabel: string;
}

@Component({
  selector: 'etxe-agent-office-hours-display',
  imports: [MatIconModule, AuraOpeningHoursDisplayComponent],
  templateUrl: './agent-office-hours-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentOfficeHoursDisplayComponent {
  readonly profile = input<AgentProfileDto | null | undefined>(undefined);
  readonly officeAddress = input<AgentOfficeAddressDto | undefined>(undefined);
  readonly openingHours = input<OpeningHoursDto | undefined>(undefined);

  readonly resolvedAddress = computed<ResolvedAddressDisplay | null>(() => {
    const prof = this.profile();
    const custom = this.officeAddress() ?? prof?.work?.officeAddress;

    if (custom && (custom.streetName || custom.city)) {
      const line1 = [custom.streetNumber, custom.streetType, custom.streetName].filter(Boolean).join(' ');
      const line2 = [custom.zipCode, custom.city].filter(Boolean).join(' ');
      return {
        line1: line1 || custom.city || 'Adresse personnalisée',
        line2,
        complement: custom.complement,
        isCustom: true,
        sourceLabel: 'Adresse personnalisée',
      };
    }

    const cardAddr = prof?.cards?.activeCard?.address;
    if (cardAddr && (cardAddr.streetName || cardAddr.city)) {
      const line1 = [cardAddr.streetNumber, cardAddr.streetType, cardAddr.streetName]
        .filter(Boolean)
        .join(' ');
      const line2 = [cardAddr.zipCode, cardAddr.city].filter(Boolean).join(' ');
      return {
        line1: line1 || cardAddr.city || 'Adresse du siège Carte T',
        line2,
        complement: cardAddr.complement,
        isCustom: false,
        sourceLabel: 'Adresse officielle Carte T',
      };
    }

    return null;
  });

  readonly resolvedHours = computed<OpeningHoursDto | undefined>(() => {
    return this.openingHours() ?? this.profile()?.work?.openingHours;
  });

  readonly mapsUrl = computed<string | null>(() => {
    const addr = this.resolvedAddress();
    if (!addr) return null;
    const query = encodeURIComponent(`${addr.line1}, ${addr.line2}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  });
}
